import {
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  output,
  resource,
  signal,
  untracked,
} from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { PermissionMatrixComponent } from '@mb/auth/client';
import type {
  RoleWithScopedPermissionsDto,
  ScopedPermissionDto,
} from '@mb/auth/shared';
import { HlmButtonImports } from '@mb/ui/button';
import { HlmButtonGroupImports } from '@mb/ui/button-group';
import { HlmFieldImports } from '@mb/ui/field';
import { HlmTabsImports } from '@mb/ui/tabs';
import {
  OrgChartApiService,
  type OrgRoleOption,
} from '../../services/org-chart-api.service';
import { clientLogger, isAbortError } from '@mb/common/client';
import { DetailsShellComponent } from '@mb/components';
import { OrgRolePickerComponent } from './org-role-picker.component';
import { saveErrorKey } from './placement-error';
import { sameRoles, syncPositionRoles } from './position-roles';

/**
 * The panel behind a position's shield: what its roles grant, all together
 * and one role per tab.
 *
 * Since 2026-10-31 the roles can be changed here as well, at the customer's
 * request — the same picker as in the position's form, above the tabs. The
 * tabs follow the selection at once, before anything is saved, so the
 * effect of a role is seen while choosing it; Save then writes the set.
 * The panel stays open on what was saved.
 *
 * Only reached through the shield, which a position shows only to somebody
 * who may manage it; the server checks the same again on every request.
 */
@Component({
  selector: 'org-position-permissions-panel',
  standalone: true,
  imports: [
    TranslocoPipe,
    PermissionMatrixComponent,
    HlmButtonImports,
    HlmButtonGroupImports,
    HlmFieldImports,
    HlmTabsImports,
    DetailsShellComponent,
    OrgRolePickerComponent,
  ],
  template: `
    <app-details-shell>
      <h2 detailsTrail class="truncate font-medium text-foreground">
        {{ 'organigram.panel.title.viewPermissions' | transloco }}
      </h2>

      <hlm-button-group detailsActions>
        <button
          type="button"
          hlmBtn
          variant="outline"
          [disabled]="saving()"
          (click)="cancelled.emit()"
        >
          {{ 'organigram.panel.actions.cancel' | transloco }}
        </button>
        <button
          type="button"
          hlmBtn
          [disabled]="saving() || !changed()"
          (click)="save()"
        >
          {{
            (saving()
              ? 'organigram.panel.actions.saving'
              : 'organigram.panel.actions.save'
            ) | transloco
          }}
        </button>
      </hlm-button-group>

      <div hlmField class="mb-4">
        <label hlmFieldLabel>{{
          'organigram.panel.fields.roles' | transloco
        }}</label>
        <org-role-picker [roles]="roleOptions()" [(selected)]="selected" />
      </div>

      @if (saveError(); as err) {
        <div
          class="mb-4 rounded-md border border-destructive/20 bg-destructive/10 p-4"
        >
          <p class="text-sm text-destructive">{{ err | transloco }}</p>
        </div>
      }

      @if (loading()) {
        <div class="flex items-center justify-center py-8">
          <p class="text-sm text-muted-foreground">
            {{ 'common.loading' | transloco }}
          </p>
        </div>
      } @else if (error()) {
        <div
          class="rounded-md border border-destructive/20 bg-destructive/10 p-4"
        >
          <p class="text-sm text-destructive">{{ error() | transloco }}</p>
        </div>
      } @else if (roles().length === 0) {
        <div class="rounded-md border border-border bg-muted p-4">
          <p class="text-sm text-muted-foreground">
            {{ 'organigram.panel.noRoles' | transloco }}
          </p>
        </div>
      } @else if (entities(); as entities) {
        @if (roles().length === 1) {
          @let role = roles()[0];
          <div class="space-y-2">
            <h3 class="text-sm font-semibold text-foreground">
              {{ role.name }}
            </h3>
            @if (role.description) {
              <p class="text-sm text-muted-foreground">
                {{ role.description }}
              </p>
            }
            <div class="mt-4">
              <app-permission-matrix
                [entities]="entities"
                [permissions]="role.scopedPermissions"
              />
            </div>
          </div>
        } @else {
          <!--
            Bound, so that taking away the role whose tab is open falls back
            to "all" instead of to a tab that is no longer there.
          -->
          <hlm-tabs [tab]="shownTab()" (tabActivated)="activeTab.set($event)">
            <hlm-tabs-list variant="line" class="w-full">
              <button hlmTabsTrigger="all">
                {{ 'organigram.panel.permissions.all' | transloco }}
              </button>
              @for (role of roles(); track role.id) {
                <button [hlmTabsTrigger]="role.id">{{ role.name }}</button>
              }
            </hlm-tabs-list>

            <div hlmTabsContent="all" class="pt-4">
              <app-permission-matrix
                [entities]="entities"
                [permissions]="mergedPermissions()"
              />
            </div>

            @for (role of roles(); track role.id) {
              <div [hlmTabsContent]="role.id" class="space-y-2 pt-4">
                @if (role.description) {
                  <p class="text-sm text-muted-foreground">
                    {{ role.description }}
                  </p>
                }
                <app-permission-matrix
                  [entities]="entities"
                  [permissions]="role.scopedPermissions"
                />
              </div>
            }
          </hlm-tabs>
        }
      }
    </app-details-shell>
  `,
})
export class PositionPermissionsPanelComponent {
  readonly #api = inject(OrgChartApiService);

  readonly positionId = input.required<string>();
  /** The roles the position has, as saved. */
  readonly roleIds = input.required<string[]>();
  /** Every role it could have. */
  readonly roleOptions = input<readonly OrgRoleOption[]>([]);

  /** The new set was saved; the chart reloads around the open panel. */
  readonly saved = output<string[]>();
  readonly cancelled = output<void>();

  /** What is picked; starts from, and is measured against, what is saved. */
  readonly selected = linkedSignal<string[]>(() => [...this.roleIds()]);
  readonly changed = computed(
    () => !sameRoles(this.selected(), this.roleIds()),
  );

  readonly saving = signal(false);
  readonly saveError = signal<string | null>(null);

  readonly activeTab = signal('all');
  readonly shownTab = computed(() => {
    const active = this.activeTab();
    return this.roles().some((role) => role.id === active) ? active : 'all';
  });

  /** The catalogue the matrix is laid out by; the same for every role. */
  readonly #entitiesResource = resource({
    loader: ({ abortSignal }) =>
      this.#api.getScopedEntities({ signal: abortSignal }),
  });

  /**
   * Each role's permissions, fetched once and kept.
   *
   * Per role rather than for the whole selection: picking a role then loads
   * that one tab, instead of blanking the panel while every role already on
   * screen is fetched again.
   */
  readonly #loaded = signal<ReadonlyMap<string, RoleWithScopedPermissionsDto>>(
    new Map(),
  );
  readonly #pending = signal<ReadonlySet<string>>(new Set());

  constructor() {
    const aborter = new AbortController();
    inject(DestroyRef).onDestroy(() => aborter.abort());

    effect(() => {
      const wanted = this.selected();
      untracked(() => {
        for (const roleId of wanted) {
          if (this.#loaded().has(roleId) || this.#pending().has(roleId)) {
            continue;
          }
          void this.#load(roleId, aborter.signal);
        }
      });
    });
  }

  async #load(roleId: string, signal: AbortSignal): Promise<void> {
    this.#pending.update((ids) => new Set(ids).add(roleId));
    try {
      const role = await this.#api.getRoleWithPermissions(roleId, { signal });
      this.#loaded.update((roles) => new Map(roles).set(roleId, role));
    } catch (error) {
      // A role that cannot be read is left off the tabs, as it always was;
      // the others still show what they grant.
      if (!isAbortError(error)) {
        clientLogger.error('Failed to load permissions', {
          error: error as Error,
        });
      }
    } finally {
      this.#pending.update((ids) => {
        const next = new Set(ids);
        next.delete(roleId);
        return next;
      });
    }
  }

  /** The picked roles whose permissions have arrived, in the picked order. */
  readonly roles = computed(() => {
    const loaded = this.#loaded();
    return this.selected()
      .map((roleId) => loaded.get(roleId))
      .filter((role): role is RoleWithScopedPermissionsDto => !!role);
  });

  readonly entities = computed(() =>
    this.#entitiesResource.hasValue() ? this.#entitiesResource.value() : null,
  );

  /**
   * Blank only while nothing can be shown yet. A role picked later loads
   * behind the tabs already there, which stay.
   */
  readonly loading = computed(
    () =>
      this.selected().length > 0 &&
      (this.#entitiesResource.isLoading() ||
        (this.roles().length === 0 && this.#pending().size > 0)),
  );
  readonly error = computed(() =>
    this.#entitiesResource.status() === 'error'
      ? 'organigram.panel.errors.loadingPermissions'
      : null,
  );

  readonly mergedPermissions = computed((): ScopedPermissionDto[] => {
    const seen = new Set<string>();
    const merged: ScopedPermissionDto[] = [];
    for (const role of this.roles()) {
      for (const perm of role.scopedPermissions) {
        const key = `${perm.entity}|${perm.action}|${perm.scope}`;
        if (!seen.has(key)) {
          seen.add(key);
          merged.push(perm);
        }
      }
    }
    return merged;
  });

  async save(): Promise<void> {
    if (!this.changed() || this.saving()) return;
    const after = [...this.selected()];
    this.saving.set(true);
    this.saveError.set(null);
    try {
      await syncPositionRoles(this.#api, this.positionId(), this.roleIds(), after);
      this.saved.emit(after);
    } catch (error) {
      clientLogger.error('Failed to save the roles of a position', {
        error: error instanceof Error ? error : new Error(String(error)),
      });
      this.saveError.set(saveErrorKey(error));
    } finally {
      this.saving.set(false);
    }
  }
}
